import { IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

/**
 * Row-action icon buttons for the document portal list pages (Proposal,
 * Chemical Tracker, Tally Doc, Pulling Report, Additional Documents — Admin and
 * Client variants alike).
 *
 * These reproduce what the pre-migration DocumentsPortalLayout rendered: 30x30
 * rounded icon buttons with a tooltip and a hover state, rather than the bare
 * unstyled <button>Download</button> elements the first migration pass left
 * behind. Keeping them in one place is what makes all ten pages consistent.
 */

interface ActionProps {
  onClick: () => void;
  /** Row identifier, used to build a stable aria-label (e.g. `download-u-12`). */
  rowId: string | number;
  /** In-flight guard — disables the control and shows a wait cursor. */
  busy?: boolean;
  title?: string;
}

const actionSx = {
  width: 30,
  height: 30,
  borderRadius: "7px",
  border: "1px solid",
  borderColor: "divider",
  color: "text.secondary",
  "&:hover": { borderColor: "secondary.main", color: "secondary.main" },
};

const dangerSx = {
  ...actionSx,
  "&:hover": { borderColor: "error.main", color: "error.main" },
};

export const DownloadAction = ({ onClick, rowId, busy = false, title = "Download" }: ActionProps) => (
  <Tooltip title={title} arrow>
    {/* A disabled MUI button emits no events, so Tooltip needs a real wrapper. */}
    <span style={{ display: "inline-flex", cursor: busy ? "wait" : "auto" }}>
      <IconButton
        size="small"
        onClick={() => !busy && onClick()}
        disabled={busy}
        aria-label={`download-${rowId}`}
        sx={{ ...actionSx, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.5 : 1 }}
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
);

export const OpenAction = ({ onClick, rowId, title = "Open in new tab" }: ActionProps) => (
  <Tooltip title={title} arrow>
    <IconButton size="small" onClick={onClick} aria-label={`open-${rowId}`} sx={actionSx}>
      <OpenInNewIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const EditAction = ({ onClick, rowId, title = "Edit" }: ActionProps) => (
  <Tooltip title={title} arrow>
    <IconButton size="small" onClick={onClick} aria-label={`edit-${rowId}`} sx={actionSx}>
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const DeleteAction = ({ onClick, rowId, title = "Delete" }: ActionProps) => (
  <Tooltip title={title} arrow>
    <IconButton size="small" onClick={onClick} aria-label={`delete-${rowId}`} sx={dangerSx}>
      <DeleteOutlineIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);
